"use client"

import { ChevronDown, Share04 } from "@untitledui/icons"
import type { FC, HTMLAttributes, MouseEventHandler, ReactNode } from "react"
import { Link as AriaLink } from "react-aria-components"

import { Badge } from "@/components/ui/badge"

import { cx, sortCx } from "@uui/utils/cx"

// On the Manila rail, the active item is a Paper White "proof" pulled forward:
// it lifts off the rail with shadow-xs and carries its weight in ink — no
// yellow furniture (the Highlighter Rule keeps the one yellow moment for the
// page's primary action) and no coloured side-stripe. Hover is a quieter half-
// paper wash; the active state is distinguished by full paper + shadow + weight.
const styles = sortCx({
  root: "group relative flex w-full cursor-pointer items-center rounded-lg outline-focus-ring transition duration-100 ease-linear select-none hover:bg-surface-elevated/60 focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
  rootSelected: "bg-surface-elevated shadow-xs hover:bg-surface-elevated",
})

interface NavItemBaseProps {
  /** Whether the nav item shows only an icon. */
  iconOnly?: boolean
  /** Whether the collapsible nav item is open. */
  open?: boolean
  /** URL to navigate to when the nav item is clicked. */
  href?: string
  /** Type of the nav item. */
  type: "link" | "collapsible" | "collapsible-child"
  /** Icon component to display. */
  icon?: FC<HTMLAttributes<HTMLOrSVGElement>>
  /** Badge to display. */
  badge?: ReactNode
  /** Whether the nav item is currently active. */
  current?: boolean
  /** Whether to truncate the label text. */
  truncate?: boolean
  /** Handler for click events. */
  onClick?: MouseEventHandler
  /** Content to display. */
  children?: ReactNode
}

export const NavItem = ({
  current,
  type,
  badge,
  href,
  icon: Icon,
  children,
  truncate = true,
  onClick,
}: NavItemBaseProps) => {
  const iconElement = Icon && (
    <Icon
      aria-hidden="true"
      className={cx(
        "text-fg-quaternary transition-inherit-all group-hover:text-fg-secondary mr-2.5 size-5 shrink-0",
        current && "text-fg-secondary",
      )}
    />
  )

  const badgeElement =
    badge && (typeof badge === "string" || typeof badge === "number") ? (
      <Badge className="ml-3" color="gray" type="pill-color" size="sm">
        {badge}
      </Badge>
    ) : (
      badge
    )

  const labelElement = (
    <span
      className={cx(
        "text-secondary transition-inherit-all group-hover:text-primary flex-1 text-sm font-medium",
        truncate && "truncate",
        current && "text-primary font-semibold",
      )}
    >
      {children}
    </span>
  )

  const isExternal = href?.startsWith("http")
  const externalIcon = isExternal && (
    <Share04 className="text-fg-quaternary size-4 stroke-[2.5px]" />
  )

  if (type === "collapsible") {
    return (
      <summary
        className={cx(
          "px-3 py-2.5",
          styles.root,
          current && styles.rootSelected,
        )}
        onClick={onClick}
      >
        {iconElement}

        {labelElement}

        {badgeElement}

        <ChevronDown
          aria-hidden="true"
          className="text-fg-quaternary ml-3 size-4 shrink-0 stroke-[2.5px] in-open:-scale-y-100"
        />
      </summary>
    )
  }

  if (type === "collapsible-child") {
    return (
      <AriaLink
        href={href}
        target={isExternal ? "_blank" : "_self"}
        rel="noopener noreferrer"
        className={cx(
          "py-2 pr-3 pl-10",
          styles.root,
          current && styles.rootSelected,
        )}
        onClick={onClick}
        aria-current={current ? "page" : undefined}
      >
        {labelElement}
        {externalIcon}
        {badgeElement}
      </AriaLink>
    )
  }

  return (
    <AriaLink
      href={href}
      target={isExternal ? "_blank" : "_self"}
      rel="noopener noreferrer"
      className={cx("px-3 py-2.5", styles.root, current && styles.rootSelected)}
      onClick={onClick}
      aria-current={current ? "page" : undefined}
    >
      {iconElement}
      {labelElement}
      {externalIcon}
      {badgeElement}
    </AriaLink>
  )
}
